/**
 * How far through the deck the runner is — ROUTE_INVENTORY §2.11 #59.
 *
 * Two renderings of one number, and which you get is a property of the DECK,
 * not a prop: up to 18 concepts you get a dot per concept, above that a
 * compact `n / N` plus a bar, because nineteen dots on a phone are a smear.
 *
 * The clamp is the part worth extracting. When the last concept is rated,
 * `index` equals `total` for a tick before the phase flips to results — so the
 * naive reading is "Concept 21 of 20" and an out-of-range dot. Pure, so the
 * boundary can be tabled rather than raced.
 */

/** Above this many concepts, dots give way to a compact counter. */
export const COMPACT_THRESHOLD = 18;

export interface ProgressState {
  /** Index clamped into the deck, so it can always address a real concept. */
  safeIndex: number;
  /** 1-based position, for display and for the accessible name. */
  position: number;
  total: number;
  /** `dots` renders one marker per concept; `compact` renders `n / N`. */
  kind: 'dots' | 'compact' | 'empty';
  /** Fraction complete, 0–100, for the compact bar. */
  percent: number;
  label: string;
}

export function progressState(index: number, total: number): ProgressState {
  if (total <= 0) {
    return {
      safeIndex: 0,
      position: 0,
      total: 0,
      kind: 'empty',
      percent: 0,
      label: 'No concepts',
    };
  }
  // Clamped on BOTH ends: past-the-end for the tick after the final rating,
  // and at zero because a negative index would address nothing.
  const safeIndex = Math.min(Math.max(index, 0), total - 1);
  return {
    safeIndex,
    position: safeIndex + 1,
    total,
    kind: total > COMPACT_THRESHOLD ? 'compact' : 'dots',
    percent: (safeIndex / total) * 100,
    label: `Concept ${safeIndex + 1} of ${total}`,
  };
}
